import CalendarDay from '@app/state/calendar/CalendarDay';
import { MarketStateStatDateSnapshot } from '@app/model/market_state/MarketStateStatDateSnapshot';
import { MarketStateStatDateCompetitorSnapshot } from '@app/model/market_state/MarketStateStatDateCompetitorSnapshot';
import PriceRecommendationStateSnapshot from '@app/model/market_state/output/PriceRecommendationStateSnapshot';
import { PriceRecommendationState } from '@app/model/market_state/PriceRecommendationState';

export default class MarketStateDailySnapshotWithComparisonOutput {
	statDate: string;
	current: MarketStateStatDateSnapshot;
	previous: MarketStateStatDateSnapshot | undefined;
	recommendationMarketUpdateDate?: string | null;
	calendarDay: CalendarDay;
	priceRecommendationStateSnapshot: PriceRecommendationStateSnapshot | undefined;
	allPriceRecommendationStates: PriceRecommendationState[] | undefined
	submittedRecommendationStateIds: number[]

	constructor(data: MarketStateDailySnapshotWithComparisonOutput) {
		this.statDate = data.statDate;
		this.recommendationMarketUpdateDate = data.recommendationMarketUpdateDate;
		this.current = new MarketStateStatDateSnapshot(data.current);
		this.previous = !!data.previous ? new MarketStateStatDateSnapshot(data.previous) : undefined;
		this.calendarDay = data.calendarDay;
		this.priceRecommendationStateSnapshot = !!data.priceRecommendationStateSnapshot ? new PriceRecommendationStateSnapshot(data.priceRecommendationStateSnapshot) : undefined;
		this.allPriceRecommendationStates = data.allPriceRecommendationStates?.map(item => new PriceRecommendationState(item));
		this.submittedRecommendationStateIds = data.submittedRecommendationStateIds ?? [];
	}

	public hasRecommendation() {
		return !!this.priceRecommendationStateSnapshot;
	}

	public needsRefreshResults() {
		return !this.current || this.current.isEmpty();
	}

	public findMainHotelIndex() {
		return this.current.competitorSnapshots.findIndex(s=>s.isMainHotel);
	}

	public getRecommendation() {
		return this.priceRecommendationStateSnapshot?.recommendation;
	}

	public findRecommendationIndex() {
		if (!this.hasRecommendation()) return undefined;
		const recommendationStateSnapshot = this.priceRecommendationStateSnapshot;
		const recommendedPrice = recommendationStateSnapshot?.recommendedPrice;
		if (recommendedPrice === undefined || recommendedPrice === null) return undefined;

		const snapshots = this.current.competitorSnapshots;
		const index = snapshots.findIndex(snapshot => !snapshot.isAvailable()
			|| Number(snapshot.getLowestItemForRoom()?.lowestItem?.item?.price?.amount ?? 0) < recommendedPrice);

		return index === -1 ? snapshots.length : index;

	}

	public async getCompetitorSnapshotsComparison(findPreviousMarketStateForDay: (statDate: string, snapshotLatestUpdateDate: string) => Promise<MarketStateStatDateSnapshot | undefined>): Promise<{
		current: MarketStateStatDateCompetitorSnapshot,
		previous: MarketStateStatDateCompetitorSnapshot | undefined
	}[]> {
		// If we don't have previous data, fetch it once for all competitors
		if (!this.previous) {
			try {
				const snap = await findPreviousMarketStateForDay(this.current.statDate, this.current.latestUpdateDate);
				if (snap) {
					this.previous = new MarketStateStatDateSnapshot(snap);
				}
			} catch (error) {
				console.warn('Failed to fetch previous market state for comparison:', error);
				// Continue without previous data
			}
		}

		// Now map synchronously since we have all the data we need
		return this.current.competitorSnapshots.map(item => {
			const previous = this.previous?.findDailyCompetitorSnapshot(item.competitorId);
			return {
				current: item,
				previous: previous,
			};
		});
	}
}
